import { isDefined } from "@clipboard-health/util-ts";
import { useDebounce } from "@src/appV2/lib/utils/useDebounce";
import { useMemo, useState } from "react";

import { type FacilityWithDistance } from "../../../data/schemas";

interface UseEarlyAccessListViewProps {
  facilities: FacilityWithDistance[];
  workerLocation: { lat: number; lng: number } | undefined;
}

interface FacilityGroup {
  facilities: FacilityWithDistance[];
  totalCount: number;
}

/**
 * The distance threshold in miles for a facility to be considered "near me".
 */
const NEAR_ME_DISTANCE_THRESHOLD_IN_MILES = 50;

export function useEarlyAccessListView(props: UseEarlyAccessListViewProps) {
  const { facilities, workerLocation } = props;
  const [searchTerm, setSearchTerm] = useState("");

  const debouncedSearchTerm = useDebounce({
    value: searchTerm,
    debounceTimeInMs: 500,
  });

  // Filter facilities by search term
  const filteredFacilities = useMemo(() => {
    if (!debouncedSearchTerm) {
      return facilities;
    }

    const lowerSearchTerm = debouncedSearchTerm.toLowerCase();
    return facilities.filter((facility) => {
      const matchesName = facility.name.toLowerCase().includes(lowerSearchTerm);
      const matchesAddress = facility.address.toLowerCase().includes(lowerSearchTerm);
      const matchesCity = facility.city.toLowerCase().includes(lowerSearchTerm);
      return matchesName || matchesAddress || matchesCity;
    });
  }, [facilities, debouncedSearchTerm]);

  // Group facilities into "Near Me" (within 50 miles) and "All Other"
  const groupedFacilities = useMemo(() => {
    const nearMeGroup: FacilityGroup = {
      facilities: [],
      totalCount: 0,
    };

    const allOtherGroup: FacilityGroup = {
      facilities: [],
      totalCount: 0,
    };

    filteredFacilities.forEach((facility) => {
      if (
        workerLocation &&
        isDefined(facility.distance) &&
        facility.distance <= NEAR_ME_DISTANCE_THRESHOLD_IN_MILES
      ) {
        nearMeGroup.facilities.push(facility);
      } else {
        allOtherGroup.facilities.push(facility);
      }
    });

    nearMeGroup.totalCount = nearMeGroup.facilities.length;
    allOtherGroup.totalCount = allOtherGroup.facilities.length;

    return { nearMeGroup, allOtherGroup };
  }, [filteredFacilities, workerLocation]);

  return {
    searchTerm,
    setSearchTerm,
    debouncedSearchTerm,
    nearMeGroup: groupedFacilities.nearMeGroup,
    allOtherGroup: groupedFacilities.allOtherGroup,
  };
}
